/**
 * @page DailySettlementPage
 * @description 每日日結頁（UI-4-3）
 * 確認收支、輸入日結金額、關閉今日帳務
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { safeFormatDate } from '../../lib/format';
import { api } from '../../lib/api';
import type { DailySessionDto, AccountingCashSummary } from '@pokerroom/types';

export default function DailySettlementPage() {
  const { t } = useTranslation('accounting');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [closingBalance, setClosingBalance] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const { data: session } = useQuery<DailySessionDto | null>({
    queryKey: ['accounting-active-session'],
    queryFn: () => api.get<DailySessionDto | null>('/accounting/sessions/active'),
  });

  const { data: summary, isError: summaryError } = useQuery<AccountingCashSummary>({
    queryKey: ['accounting-summary', session?.id],
    queryFn: () => api.get(`/accounting/sessions/${session!.id}/summary`),
    enabled: !!session?.id,
  });

  const closeMutation = useMutation({
    mutationFn: () => api.post(`/accounting/sessions/${session!.id}/close`, { closingBalance }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['accounting-active-session'] });
      navigate(`/accounting/report?sessionId=${session!.id}`);
    },
    onError: (err) => {
      console.warn('[DailySettlement] close failed:', (err as Error).message);
    },
  });

  if (!session) {
    return (
      <div className="p-6 max-w-lg mx-auto text-center">
        <div className="text-4xl mb-3">📒</div>
        <h2 className="text-xl font-bold text-base-content mb-2">{t('noActiveSession')}</h2>
        <a href="/accounting/open" className="px-6 py-2 rounded-md bg-primary text-primary-content">
          {t('goToOpen')}
        </a>
      </div>
    );
  }

  if (!summary)
    return <p role="status">{t(summaryError ? 'cashSummaryFailed' : 'cashSummaryLoading')}</p>;
  const totalIncome = Number(summary.totalIncome),
    totalExpense = Number(summary.totalExpense);
  const theoreticalBalance = Number(summary.expectedBalance);

  return (
    <div className="p-6 max-w-xl mx-auto">
      <h1 className="text-2xl font-bold text-base-content mb-6">{t('closeSession')}</h1>

      {/* 帳務摘要 */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6 space-y-3 text-sm">
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('sessionDate')}</span>
          <span className="font-medium">
            {safeFormatDate(session.sessionDate, (d) =>
              d.toLocaleDateString(undefined, { timeZone: 'UTC' }),
            )}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-base-content/60">{t('openingBalance')}</span>
          <span className="font-mono">
            NT${parseFloat(session.openingBalance).toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between text-emerald-600">
          <span>+ {t('totalIncome')}</span>
          <span className="font-mono">NT${totalIncome.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-red-600">
          <span>- {t('totalExpense')}</span>
          <span className="font-mono">NT${totalExpense.toLocaleString()}</span>
        </div>
        <hr className="border-base-300" />
        <div className="flex justify-between font-medium text-base">
          <span>{t('theoreticalBalance')}</span>
          <span className="font-mono text-primary">NT${theoreticalBalance.toLocaleString()}</span>
        </div>
      </div>

      <p className="mb-4 text-sm">
        {t('dayBases', {
          accounting: summary.accountingBusinessDate,
          zipa: summary.zipaBusinessDate,
        })}
      </p>
      <p className="mb-4 text-sm">
        {t('noncashTotals', { income: summary.nonCashIncome, expense: summary.nonCashExpense })}
      </p>
      {session.isCurrentBusinessDate === false && <p role="alert">{t('historicalOpenSession')}</p>}
      {/* 實際日結金額 */}
      <div className="bg-base-200 border border-base-300 rounded-xl p-5 mb-6">
        <label className="block text-sm text-base-content/70 mb-2">
          {t('closingBalance')} (NT$) *
        </label>
        <input
          type="number"
          value={closingBalance}
          onChange={(e) => setClosingBalance(e.target.value)}
          placeholder={t('suggest', { value: theoreticalBalance.toLocaleString() })}
          className="w-full px-3 py-3 rounded-md border border-base-300 bg-base-200 text-base-content text-xl
                     font-mono focus:outline-hidden focus:ring-2 focus:ring-primary"
        />
        {closingBalance && Math.abs(parseFloat(closingBalance) - theoreticalBalance) > 0.01 && (
          <p
            className={`text-xs mt-1.5 ${parseFloat(closingBalance) > theoreticalBalance ? 'text-emerald-600' : 'text-red-600'}`}
          >
            {t('difference')}
            {parseFloat(closingBalance) > theoreticalBalance ? '+' : ''}
            NT${(parseFloat(closingBalance) - theoreticalBalance).toLocaleString()}
          </p>
        )}
      </div>

      {/* 確認勾選 */}
      <label className="flex items-center gap-2 mb-6 cursor-pointer">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
          className="w-4 h-4 text-primary rounded border-base-300"
        />
        <span className="text-sm text-base-content">{t('confirmSettleText')}</span>
      </label>

      {closeMutation.error && (
        <div className="mb-4 px-4 py-3 bg-error/10 border border-error/30 rounded-lg text-sm text-error">
          {(closeMutation.error as { response?: { data?: { message?: string } } }).response?.data
            ?.message ?? t('closeFailed', { defaultValue: 'Failed to close session' })}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => navigate('/accounting/expenses')}
          className="flex-1 py-2.5 rounded-md border border-base-300 text-sm"
        >
          {tc('back')}
        </button>
        <button
          onClick={() => {
            const url = `/api/accounting/sessions/${session.id}/pdf`;
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          className="py-2.5 px-4 rounded-md border border-base-300 text-sm hover:bg-base-300 transition-colors"
        >
          {t('downloadPdf', { defaultValue: 'PDF' })}
        </button>
        <button
          onClick={() => closeMutation.mutate()}
          disabled={!closingBalance || !confirmed || closeMutation.isPending}
          className="flex-1 py-2.5 rounded-md bg-primary text-primary-content text-sm font-medium disabled:opacity-50"
        >
          {closeMutation.isPending ? tc('loading') : t('confirmSettle')}
        </button>
      </div>
    </div>
  );
}
